import { ReactNode, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { useState } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AddIcon from '@mui/icons-material/Add';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InsightsIcon from '@mui/icons-material/Insights';
// ChevronLeft/Right removed — sidebar is now hover-only, no toggle button
import SettingsIcon from '@mui/icons-material/Settings';
import BadgeIcon from '@mui/icons-material/Badge';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
// AssessmentIcon removed — Overview page removed
import BarChartIcon from '@mui/icons-material/BarChart';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../contexts/AuthContext';
import { useHrPendingCounts } from '../../hooks/useHrPendingCounts';
import type { HrPendingSummary } from '../../services/salary.service';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useSidebar } from '../../App';
import { UserRole } from '../../types';

const DRAWER_WIDTH = 220;
const COLLAPSED_WIDTH = 56;

interface NavItem {
  title: string;
  path: string;
  icon: ReactNode;
  roles?: UserRole[];
  section?: string;
  featureFlag?: 'dashboard' | 'trades' | 'clients' | 'riskMonitor' | 'payouts' | 'analytics';
  isHRFeature?: boolean; // HR features are always enabled
  // HR revamp: kind of pending-work badge to render to the right of the label.
  // Resolved against useHrPendingCounts() inside the component.
  badgeKind?: 'hr-any' | 'attendance' | 'payroll';
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    path: '/',
    icon: <DashboardIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT],
    section: 'main',
    featureFlag: 'dashboard',
  },
  {
    title: 'All Trades',
    path: '/trades',
    icon: <ShowChartIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT],
    section: 'trades',
    featureFlag: 'trades',
  },
  {
    title: 'Import Trades',
    path: '/import',
    icon: <UploadFileIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER],
    section: 'trades',
    featureFlag: 'trades',
  },
  {
    title: 'Market',
    path: '/market',
    icon: <CandlestickChartIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT],
    section: 'trades',
    featureFlag: 'dashboard',
  },
  {
    title: 'Clients',
    path: '/clients',
    icon: <PeopleOutlineIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER, UserRole.CLIENT],
    section: 'management',
    featureFlag: 'clients',
  },
  {
    title: 'Risk Monitor',
    path: '/risk',
    icon: <WarningAmberIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.DEALER],
    section: 'management',
    featureFlag: 'riskMonitor',
  },
  {
    title: 'Payouts',
    path: '/payouts',
    icon: <AccountBalanceWalletIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    section: 'management',
    featureFlag: 'payouts',
  },
  {
    title: 'Client Analytics',
    path: '/analytics',
    icon: <InsightsIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT, UserRole.CLIENT],
    section: 'reports',
    featureFlag: 'analytics',
  },
  {
    title: 'Dealer Analytics',
    path: '/dealer-analytics',
    icon: <BarChartIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.AGENT],
    section: 'reports',
    featureFlag: 'analytics',
  },
  {
    title: 'HR Home',
    path: '/hr',
    icon: <HomeOutlinedIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.HR],
    section: 'hr',
    isHRFeature: true,
    badgeKind: 'hr-any',
  },
  {
    title: 'Employees',
    path: '/employees',
    icon: <BadgeIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.HR],
    section: 'hr',
    isHRFeature: true,
  },
  {
    title: 'Attendance',
    path: '/attendance',
    icon: <EventNoteIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.HR],
    section: 'hr',
    isHRFeature: true,
    badgeKind: 'attendance',
  },
  {
    title: 'Payroll',
    path: '/payroll',
    icon: <ReceiptLongIcon />,
    roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.HR],
    section: 'hr',
    isHRFeature: true,
    badgeKind: 'payroll',
  },
  {
    title: 'User Management',
    path: '/users',
    icon: <AdminPanelSettingsIcon />,
    roles: [UserRole.SUPER_ADMIN],
    section: 'admin',
    isHRFeature: false,
  },
  {
    title: 'My Payslips',
    path: '/my-payslips',
    icon: <DescriptionIcon />,
    roles: [UserRole.EMPLOYEE],
    section: 'employee',
    isHRFeature: true,
  },
  {
    title: 'Employee Portal',
    path: '/employee-portal',
    icon: <DashboardIcon />,
    roles: [UserRole.EMPLOYEE],
    section: 'employee',
    isHRFeature: false,
  },
  {
    title: 'Settings',
    path: '/settings',
    icon: <SettingsIcon />,
    section: 'settings',
  },
];

// Friendly role label for the sidebar user card. Without this, the raw enum
// string (e.g., 'hr', 'dealer') would leak into the UI.
function formatRoleLabel(role: UserRole | undefined | null): string {
  switch (role) {
    case UserRole.SUPER_ADMIN: return 'Super Admin';
    case UserRole.ADMIN: return 'Admin';
    case UserRole.HR: return 'HR';
    case UserRole.DEALER: return 'Dealer';
    case UserRole.AGENT: return 'Agent';
    case UserRole.CLIENT: return 'Client';
    case UserRole.EMPLOYEE: return 'Employee';
    default: return 'Guest';
  }
}

// Decide whether a nav item should show a red pending-work dot based on the
// HR Home counters. Returns false if hrPending is null (employee/dealer user).
function shouldShowBadge(item: NavItem, hrPending: HrPendingSummary | null): boolean {
  if (!hrPending || !item.badgeKind) return false;
  if (item.badgeKind === 'attendance') return hrPending.unmarkedAttendanceToday > 0;
  if (item.badgeKind === 'payroll') {
    return hrPending.draftSlipsThisMonth + hrPending.generatedSlipsThisMonth + hrPending.pendingApprovalSlipsThisMonth > 0;
  }
  // 'hr-any' — show dot if any pending work anywhere
  return (
    hrPending.unmarkedAttendanceToday > 0 ||
    hrPending.draftSlipsThisMonth + hrPending.generatedSlipsThisMonth + hrPending.pendingApprovalSlipsThisMonth > 0
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSuperAdmin, logout } = useAuth();
  const hrPending = useHrPendingCounts();

  // User-card popover state (Profile / Sign out menu)
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const userMenuOpen = Boolean(userMenuAnchor);
  const handleUserMenuOpen = (e: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(e.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleSignOut = async () => {
    handleUserMenuClose();
    try { await logout(); } finally { navigate('/login'); }
  };
  const handleOpenSettings = () => {
    handleUserMenuClose();
    navigate('/settings');
    setMobileOpen(false);
  };
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const { isFeatureEnabled } = useFeatureFlags();

  // Hover to expand, leave to collapse (desktop only)
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (collapsed) setCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setCollapsed(true);
    }, 300); // small delay so it doesn't flicker
  };

  const filteredNavItems = navItems.filter((item) => {
    // Check role access first
    if (item.roles && user) {
      const userRoles = user.roles || (user.role ? [user.role] : []);
      const hasAccess = userRoles.some(role => item.roles?.includes(role));
      if (!hasAccess) return false;
    } else if (item.roles && !user) {
      return false;
    }

    // HR features are always enabled regardless of feature flags
    if (item.isHRFeature) return true;

    // For HR-only users (no SUPER_ADMIN, ADMIN, or EMPLOYEE), only show HR features and settings
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    const isHROnly = userRoles.includes(UserRole.HR) &&
                    !userRoles.includes(UserRole.SUPER_ADMIN) &&
                    !userRoles.includes(UserRole.ADMIN) &&
                    !userRoles.includes(UserRole.EMPLOYEE) &&
                    !userRoles.includes(UserRole.AGENT);
    if (isHROnly) {
      // HR-only users: show HR features, settings, but no trading features
      return item.isHRFeature || item.section === 'settings';
    }

    // Super Admin always sees everything (even if disabled, they can enable it)
    if (isSuperAdmin) return true;

    // Admin always sees all items (use roles array)
    const isAdmin = userRoles.includes(UserRole.ADMIN);
    if (isAdmin) return true;

    // For other users, check feature flags for their role
    if (item.featureFlag) {
      return isFeatureEnabled(item.featureFlag);
    }

    // Settings is always available
    if (item.section === 'settings') return true;

    return true;
  });

  // Check if a nav item is disabled (shown but not functional)
  const isItemDisabled = (item: NavItem): boolean => {
    if (item.isHRFeature) return false; // HR features always work
    if (isSuperAdmin) return false; // Super Admin can always access
    if (!item.featureFlag) return false;
    return !isFeatureEnabled(item.featureFlag);
  };

  const handleNavigation = (path: string, _item: NavItem) => {
    // If feature is disabled, still navigate but it will show ComingSoon
    navigate(path);
    setMobileOpen(false);
  };

  const currentWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Section display labels shown only when expanded
  const SECTION_LABELS: Record<string, string> = {
    main: 'Overview',
    trades: 'Trading',
    management: 'Management',
    reports: 'Reports',
    hr: 'Human Resources',
    admin: 'Administration',
    employee: 'My Portal',
    settings: '',
  };

  // Group filtered items into section buckets preserving the order sections first appear
  const sectionOrder: string[] = [];
  const sectionMap: Record<string, NavItem[]> = {};
  filteredNavItems.forEach((item) => {
    const key = item.section || 'main';
    if (!sectionMap[key]) {
      sectionMap[key] = [];
      sectionOrder.push(key);
    }
    sectionMap[key].push(item);
  });

  const canAddTrade = (() => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return (
      isSuperAdmin ||
      userRoles.includes(UserRole.ADMIN) ||
      userRoles.includes(UserRole.AGENT)
    );
  })();

  const handleAddTrade = () => {
    navigate('/?newTrade=true');
    setMobileOpen(false);
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#FFFFFF',
        borderRight: '1px solid #E5E7EB',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          px: 1,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #F3F4F6',
          cursor: 'pointer',
          minHeight: 52,
          overflow: 'hidden',
        }}
        onClick={() => navigate('/')}
      >
        <Box sx={{ position: 'relative', width: collapsed ? 32 : 140, height: 32, transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Box
            component="img"
            src="/favicon.png"
            alt="TR"
            sx={{
              position: 'absolute', left: 0, top: 0,
              width: 32, height: 32,
              opacity: collapsed ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          />
          <Box
            component="img"
            src="/logo.png"
            alt="Trading Rabbit"
            sx={{
              position: 'absolute', left: 0, top: 0,
              width: 140, height: 32,
              objectFit: 'contain', objectPosition: 'left',
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.25s ease 0.1s',
            }}
          />
        </Box>
      </Box>

      {/* Add Trade Primary Action */}
      {canAddTrade && (
        <Box sx={{ px: collapsed ? 0.75 : 1.25, pt: 1.25, pb: 0.5 }}>
          {collapsed ? (
            <Tooltip title="New Trade" placement="right" arrow>
              <Button
                fullWidth
                onClick={handleAddTrade}
                sx={{
                  minWidth: 0,
                  height: 38,
                  p: 0,
                  borderRadius: '8px',
                  bgcolor: '#0A0A0A',
                  color: '#FFFFFF',
                  boxShadow: 'none',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: '#262626',
                    boxShadow: 'none',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          ) : (
            <Button
              fullWidth
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddTrade}
              sx={{
                height: 38,
                borderRadius: '8px',
                bgcolor: '#0A0A0A',
                color: '#FFFFFF',
                fontSize: '0.83rem',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.01em',
                boxShadow: 'none',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: '#262626',
                  boxShadow: 'none',
                },
              }}
            >
              New Trade
            </Button>
          )}
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', overflowX: 'hidden', pt: 0.5 }}>
        {sectionOrder.map((sectionKey, sIdx) => {
          const items = sectionMap[sectionKey];
          const label = SECTION_LABELS[sectionKey];
          return (
            <Box key={sectionKey} sx={{ mb: 0.5 }}>
              {/* Section label (visible only expanded, skip empty labels) */}
              {!collapsed && label && (
                <Typography
                  sx={{
                    px: 2,
                    pt: sIdx === 0 ? 1.25 : 1.5,
                    pb: 0.5,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              )}
              {collapsed && sIdx > 0 && (
                <Box sx={{ mx: 1.25, my: 0.75, height: '1px', bgcolor: '#F3F4F6' }} />
              )}
              <List disablePadding sx={{ px: collapsed ? 0.5 : 1 }}>
                {items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const disabled = isItemDisabled(item);

                  const button = (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.15 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path, item)}
                        sx={{
                          borderRadius: '7px',
                          py: 0.7,
                          px: collapsed ? 1 : 1.25,
                          mx: collapsed ? 0.3 : 0,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          transition: 'background-color 0.15s ease, color 0.15s ease',
                          opacity: disabled ? 0.5 : 1,
                          position: 'relative',
                          ...(isActive
                            ? {
                                bgcolor: '#F4F4F5',
                                '&:hover': { bgcolor: '#E4E4E7' },
                                '& .MuiListItemIcon-root': { color: '#0A0A0A' },
                                '& .MuiListItemText-primary': { color: '#0A0A0A', fontWeight: 600 },
                                '&::before': !collapsed ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: -4,
                                  top: 6,
                                  bottom: 6,
                                  width: 3,
                                  borderRadius: 2,
                                  bgcolor: '#0A0A0A',
                                } : undefined,
                              }
                            : {
                                '&:hover': { bgcolor: '#FAFAFA' },
                                '& .MuiListItemIcon-root': { color: disabled ? '#A3A3A3' : '#737373' },
                                '& .MuiListItemText-primary': { color: '#404040' },
                              }),
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: collapsed ? 0 : 34,
                            justifyContent: 'center',
                            '& .MuiSvgIcon-root': { fontSize: '1.2rem' },
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.83rem',
                            fontWeight: isActive ? 600 : 500,
                            whiteSpace: 'nowrap',
                          }}
                          sx={{
                            opacity: collapsed ? 0 : 1,
                            width: collapsed ? 0 : 'auto',
                            overflow: 'hidden',
                            transition: 'opacity 0.2s ease 0.05s, width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                        {/* HR revamp: pending-work dot */}
                        {shouldShowBadge(item, hrPending) && (
                          <Box
                            component="span"
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: '#DC2626',
                              ml: 1,
                              flexShrink: 0,
                              opacity: collapsed ? 0 : 1,
                              transition: 'opacity 0.2s ease',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );

                  return collapsed ? (
                    <Tooltip key={item.path} title={item.title} placement="right" arrow>
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User Section */}
      <Box
        sx={{
          p: collapsed ? 0.5 : 1,
          borderTop: '1px solid #E5E7EB',
        }}
      >
        {(() => {
          const card = (
            <Box
              role="button"
              tabIndex={0}
              onClick={handleUserMenuOpen}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUserMenuOpen(e as any); }}
              sx={{
                p: collapsed ? 0.5 : 0.75,
                borderRadius: '8px',
                bgcolor: userMenuOpen ? '#EEF2FF' : '#F9FAFB',
                border: '1px solid',
                borderColor: userMenuOpen ? '#C7D2FE' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: collapsed ? 0 : 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#E2E8F0' },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#0A0A0A',
                  color: '#FFFFFF',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{
                overflow: 'hidden',
                flex: collapsed ? 0 : 1,
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                transition: 'opacity 0.2s ease, width 0.3s ease, flex 0.3s ease',
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.8rem',
                  }}
                >
                  {(user?.name || 'User').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B7280',
                    textTransform: 'capitalize',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatRoleLabel(user?.role)}
                </Typography>
              </Box>
            </Box>
          );
          return collapsed ? (
            <Tooltip title="Account" placement="right" arrow>
              {card}
            </Tooltip>
          ) : card;
        })()}
        <Menu
          anchorEl={userMenuAnchor}
          open={userMenuOpen}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: -1,
                minWidth: 200,
                borderRadius: 1.5,
                border: '1px solid #E2E8F0',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              },
            },
          }}
        >
          <Box sx={{ px: 1.75, py: 1, borderBottom: '1px solid #F1F5F9' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email || ''}
            </Typography>
          </Box>
          <MenuItem onClick={handleOpenSettings} sx={{ fontSize: '0.82rem', py: 0.9, gap: 1.25 }}>
            <ManageAccountsOutlinedIcon sx={{ fontSize: 18, color: '#64748B' }} />
            Profile & Settings
          </MenuItem>
          <Divider sx={{ my: 0.25 }} />
          <MenuItem
            onClick={handleSignOut}
            sx={{ fontSize: '0.82rem', py: 0.9, gap: 1.25, color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
            Sign out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Box
        ref={sidebarRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentWidth,
          flexShrink: 0,
          transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            width: currentWidth,
            flexShrink: 0,
            transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
              border: 'none',
              transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>
    </>
  );
}

export default Sidebar;
